import { request, requestPublic } from './client';
import type { Board, BoardManager, CreateBoardInput, UpdateBoardInput, SquareWithNumbers, Hit, BoardInvite, InviteParticipantResponse, ResendInviteResponse, AssignablePlayersResult, ReserveSquaresInput, ReserveSquaresResult, UnreserveSquaresInput } from '../types';

export interface DuplicateBoardInput {
  name: string;
  homeTeam: string;
  awayTeam: string;
  homeColor: string;
  awayColor: string;
  gameDate: string; // ISO date string
  copySquares?: boolean;
}

export const boardsApi = {
  async create(input: CreateBoardInput): Promise<Board> {
    return request<Board>('/api/squareup/boards', {
      method: 'POST',
      body: input,
    });
  },

  // Public endpoints (no authentication required)
  async getPublic(idOrSlug: string): Promise<Board> {
    return requestPublic<Board>(`/api/squareup/public/boards/${idOrSlug}`);
  },

  async getPublicSquares(idOrSlug: string): Promise<SquareWithNumbers[]> {
    return requestPublic<SquareWithNumbers[]>(`/api/squareup/public/boards/${idOrSlug}/squares`);
  },

  async getPublicHits(idOrSlug: string): Promise<Hit[]> {
    return requestPublic<Hit[]>(`/api/squareup/public/boards/${idOrSlug}/hits`);
  },

  async list(): Promise<Board[]> {
    return request<Board[]>('/api/squareup/boards');
  },

  async get(id: string): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}`);
  },

  async update(id: string, input: UpdateBoardInput): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}`, {
      method: 'PATCH',
      body: input,
    });
  },

  async lock(id: string): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}/lock`, {
      method: 'POST',
    });
  },

  async unlock(id: string): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}/unlock`, {
      method: 'POST',
    });
  },

  async publish(id: string): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}/publish`, {
      method: 'POST',
    });
  },

  async unpublish(id: string): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}/unpublish`, {
      method: 'POST',
    });
  },

  async complete(id: string): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}/complete`, {
      method: 'POST',
    });
  },

  async randomizeNumbers(id: string): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}/randomize_numbers`, {
      method: 'POST',
    });
  },

  async addManager(boardId: string, userId: string, role: string = 'admin'): Promise<BoardManager> {
    return request<BoardManager>(`/api/squareup/boards/${boardId}/managers`, {
      method: 'POST',
      body: { userId, role },
    });
  },

  async getManagers(boardId: string): Promise<BoardManager[]> {
    return request<BoardManager[]>(`/api/squareup/boards/${boardId}/managers`);
  },

  async removeManager(boardId: string, managerId: string): Promise<void> {
    return request<void>(`/api/squareup/boards/${boardId}/managers/${managerId}`, {
      method: 'DELETE',
    });
  },

  async removeParticipant(boardId: string, participantId: string): Promise<void> {
    return request<void>(`/api/squareup/boards/${boardId}/participants/${participantId}`, {
      method: 'DELETE',
    });
  },

  async inviteParticipant(boardId: string, email: string, options?: { message?: string; forceResend?: boolean }): Promise<InviteParticipantResponse> {
    return request<InviteParticipantResponse>(`/api/squareup/boards/${boardId}/participants/invite`, {
      method: 'POST',
      body: { email, message: options?.message, forceResend: options?.forceResend },
    });
  },

  async getInvites(boardId: string): Promise<BoardInvite[]> {
    const result = await request<{ invites: BoardInvite[] }>(`/api/squareup/boards/${boardId}/invites`);
    return result.invites;
  },

  async resendInvite(boardId: string, inviteId: string, forceResend?: boolean): Promise<ResendInviteResponse> {
    return request<ResendInviteResponse>(`/api/squareup/boards/${boardId}/invites/${inviteId}/resend`, {
      method: 'POST',
      body: { forceResend },
    });
  },

  async cancelInvite(boardId: string, inviteId: string): Promise<void> {
    return request<void>(`/api/squareup/boards/${boardId}/invites/${inviteId}`, {
      method: 'DELETE',
    });
  },

  async join(inviteCode: string): Promise<Board> {
    return request<Board>('/api/squareup/boards/join', {
      method: 'POST',
      body: { inviteCode },
    });
  },

  async acceptEmailInvite(token: string): Promise<{ success: boolean; boardId: string }> {
    return request<{ success: boolean; boardId: string }>(`/api/squareup/invites/${token}/accept`, {
      method: 'POST',
    });
  },

  async checkSlugAvailable(slug: string): Promise<boolean> {
    const result = await request<{ available: boolean }>(`/api/squareup/boards/check-slug?slug=${encodeURIComponent(slug)}`);
    return result.available;
  },

  async duplicate(id: string, input: DuplicateBoardInput): Promise<Board> {
    return request<Board>(`/api/squareup/boards/${id}/duplicate`, {
      method: 'POST',
      body: input,
    });
  },

  async delete(id: string): Promise<void> {
    return request<void>(`/api/squareup/boards/${id}`, {
      method: 'DELETE',
    });
  },

  // Enhanced Assign Mode APIs
  async getAssignablePlayers(boardId: string): Promise<AssignablePlayersResult> {
    return request<AssignablePlayersResult>(`/api/squareup/boards/${boardId}/assignable-players`);
  },

  async reserveSquares(boardId: string, input: ReserveSquaresInput): Promise<ReserveSquaresResult> {
    return request<ReserveSquaresResult>(`/api/squareup/boards/${boardId}/squares/reserve`, {
      method: 'POST',
      body: input,
    });
  },

  async unreserveSquares(boardId: string, input: UnreserveSquaresInput): Promise<void> {
    return request<void>(`/api/squareup/boards/${boardId}/squares/unreserve`, {
      method: 'POST',
      body: input,
    });
  },
};
